// Role-based access control configuration
export const ROLES = {
  ADMIN: 'admin',
  SENIOR_ADMIN: 'senior_admin',
  SYSTEM_ADMIN: 'system_admin',
  MASTER_ADMIN: 'master_admin'
};

// Define permissions for each role
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: {
    name: 'admin',
    description: 'Can access Content Generators and Profile tabs only',
    permissions: [
      'google-content-generator',
      'facebook-content-generator',
      'lemon8-content-generator',
      'xhs-content-generator',
      'xhs-content-generator-copy',
      'xhs-ai-image',
      'profile'
    ]
  },
  [ROLES.SENIOR_ADMIN]: {
    name: 'senior_admin',
    description: 'Can access all tabs except AI Generator',
    permissions: [
      'dashboard',
      'banner-images',
      'lucky-draw',
      'xhs-images',
      'instagram-images',
      'profile'
    ]
  },
  [ROLES.MASTER_ADMIN]: {
    name: 'master_admin',
    description: 'Master administrator with full access to all features',
    permissions: [
      'dashboard',
      'master-dashboard',
      'banner-images',
      'lucky-draw',
      'xhs-images',
      'instagram-images',
      'google-content-generator',
      'facebook-content-generator',
      'lemon8-content-generator',
      'xhs-content-generator',
      'xhs-content-generator-copy',
      'xhs-ai-image',
      'users',
      'profile',
      'platform-content'
    ]
  },
  [ROLES.SYSTEM_ADMIN]: {
    name: 'system_admin',
    description: 'Can access all tabs including Content Generators and Users management',
    permissions: [
      'dashboard',
      'master-dashboard',
      'banner-images',
      'lucky-draw',
      'xhs-images',
      'instagram-images',
      'google-content-generator',
      'facebook-content-generator',
      'lemon8-content-generator',
      'xhs-content-generator',
      'xhs-content-generator-copy',
      'xhs-ai-image',
      'users',
      'profile'
    ]
  }
};

// Role display info with colors for UI
export const ROLE_DISPLAY_INFO = {
  [ROLES.ADMIN]: { name: 'admin', color: '#27ae60' },
  [ROLES.SENIOR_ADMIN]: { name: 'senior_admin', color: '#f39c12' },
  [ROLES.SYSTEM_ADMIN]: { name: 'system_admin', color: '#e74c3c' },
  [ROLES.MASTER_ADMIN]: { name: 'master_admin', color: '#9b59b6' },
};

// Navigation items configuration
export const NAVIGATION_ITEMS = [
  {
    id: 'master-dashboard',
    label: 'Master Dashboard',
    icon: 'Apps',
    path: '/master-dashboard',
    requiredPermission: 'master-dashboard'
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'Dashboard',
    path: '/dashboard',
    requiredPermission: 'dashboard'
  },
  {
    id: 'banner-images',
    label: 'Banner Image',
    icon: 'Collections',
    path: '/banner-images',
    requiredPermission: 'banner-images'
  },
  {
    id: 'lucky-draw',
    label: 'Lucky Draw',
    icon: 'Redeem',
    path: '/lucky-draw',
    requiredPermission: 'lucky-draw'
  },
  {
    id: 'banner-images',
    label: 'Banner Image',
    icon: 'Collections',
    path: '/banner-images',
    requiredPermission: 'banner-images'
  },
  {
    id: 'lucky-draw',
    label: 'Lucky Draw',
    icon: 'Redeem',
    path: '/lucky-draw',
    requiredPermission: 'lucky-draw'
  },
  {
    id: 'instagram-images',
    label: 'Instagram Images',
    icon: 'PhotoCamera',
    path: '/instagram-images',
    requiredPermission: 'instagram-images'
  },
  {
    id: 'xhs-ai-image',
    label: 'AI Image Generator',
    icon: 'Image',
    path: '/xhs-ai-image',
    requiredPermission: 'xhs-ai-image'
  },
  {
    id: 'xhs',
    label: 'XHS Content Generator',
    icon: 'AutoStories',
    path: '/xhs',
    requiredPermission: 'xhs-content-generator'
  },
  {
    id: 'xhs-copy',
    label: 'XHS Content Generator',
    icon: 'AutoAwesome',
    path: '/xhs-copy',
    requiredPermission: 'xhs-content-generator-copy'
  },
  {
    id: 'google-content-generator',
    label: 'Google Content Generator',
    icon: 'LocationOn',
    path: '/google-content-generator',
    requiredPermission: 'google-content-generator'
  },
  {
    id: 'facebook-content-generator',
    label: 'Facebook Content Generator',
    icon: 'ThumbUp',
    path: '/facebook-content-generator',
    requiredPermission: 'facebook-content-generator'
  },
  {
    id: 'lemon8-content-generator',
    label: 'Lemon8 Content Generator',
    icon: 'Article',
    path: '/lemon8-content-generator',
    requiredPermission: 'lemon8-content-generator'
  },
  {
    id: 'users',
    label: 'Users',
    icon: 'People',
    path: '/users',
    requiredPermission: 'users'
  },
  {
    id: 'profile',
    label: 'Profile',
    icon: 'Person',
    path: '/profile',
    requiredPermission: 'profile'
  },
  {
    id: 'master-dashboard',
    label: 'Master Dashboard',
    icon: 'Apps',
    path: '/master-dashboard',
    requiredPermission: 'master-dashboard'
  }
];

// Helper functions
export const hasPermission = (userRole, permission) => {
  if (!userRole || !ROLE_PERMISSIONS[userRole]) {
    return false;
  }
  return ROLE_PERMISSIONS[userRole].permissions.includes(permission);
};

export const getAvailableNavigationItems = (userRole) => {
  if (!userRole || !ROLE_PERMISSIONS[userRole]) {
    return [];
  }

  return NAVIGATION_ITEMS.filter(item =>
    hasPermission(userRole, item.requiredPermission)
  );
};

export const getRoleInfo = (userRole) => {
  return ROLE_PERMISSIONS[userRole] || null;
};

// Route protection helper
export const isRouteAccessible = (userRole, routePath) => {
  const navigationItem = NAVIGATION_ITEMS.find(item => item.path === routePath);
  if (!navigationItem) {
    return false; // Unknown route
  }
  return hasPermission(userRole, navigationItem.requiredPermission);
};
